import { env } from '@/config/env'

interface SentryUser {
  id: string
  email?: string
  username?: string
}

interface SentryConfig {
  dsn?: string
  environment: string
  tracesSampleRate: number
  replaysSessionSampleRate: number
  replaysOnErrorSampleRate: number
}

const isLocal = env.VITE_APP_ENV === 'local'
const isProd = env.VITE_APP_ENV === 'production'

const config: SentryConfig = {
  dsn: env.VITE_SENTRY_DSN,
  environment: env.VITE_APP_ENV,
  // Config is only consumed when not local (init bails early otherwise), so
  // these rates apply to staging/production: full sampling on staging, leaner
  // on production; error replays on for both.
  tracesSampleRate: isProd ? 0.1 : 1.0,
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
}

// Environmental noise that is never an app bug — dropped before it's sent so it
// doesn't accrue events or quota. Entries match as substrings (or regex) against
// the error message. See the resolved/ignored Sentry issues these correspond to.
const IGNORED_ERRORS = [
  // Stale-bundle chunk-load failures after a redeploy: the client holds an old
  // build and requests a code-split chunk that no longer exists (served an HTML
  // 404 instead of JS). Self-resolves on reload. (STYLINK-BRAND-APP-7 / -D / -8.)
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
  /is not a valid JavaScript MIME type/i,
  'Expected a JavaScript module script but the server responded with a MIME type',
  // Transient network failures / aborted navigations (offline, flaky connection,
  // CORS). Not app logic bugs; expected API errors are already filtered in the
  // query client. (STYLINK-BRAND-APP-K / -H / -G.)
  'Failed to fetch',
  'NetworkError when attempting to fetch resource',
  'Load failed',
]

let isInitialized = false

export async function initSentry() {
  // Disabled for local installations (VITE_APP_ENV=local); runs in deployed
  // environments (staging/production), and only when a DSN is configured.
  if (isInitialized || !config.dsn || isLocal) {
    return
  }

  try {
    const Sentry = await import('@sentry/react')

    Sentry.init({
      dsn: config.dsn,
      environment: config.environment,
      ignoreErrors: IGNORED_ERRORS,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
      tracesSampleRate: config.tracesSampleRate,
      replaysSessionSampleRate: config.replaysSessionSampleRate,
      replaysOnErrorSampleRate: config.replaysOnErrorSampleRate,
    })

    isInitialized = true
  } catch (error) {
    console.error('Failed to initialize Sentry:', error)
  }
}

export function setUser(user: SentryUser | null) {
  if (!isInitialized) return

  void import('@sentry/react').then((Sentry) => {
    if (user) {
      Sentry.setUser(user)
    } else {
      Sentry.setUser(null)
    }
  })
}

export function captureException(error: unknown, context?: Record<string, unknown>) {
  if (!isInitialized) {
    console.error('Sentry not initialized, logging error:', error)
    return
  }

  void import('@sentry/react').then((Sentry) => {
    Sentry.captureException(error, {
      extra: context,
    })
  })
}

export function captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info') {
  if (!isInitialized) {
    console.log(`[${level}] ${message}`)
    return
  }

  void import('@sentry/react').then((Sentry) => {
    Sentry.captureMessage(message, level)
  })
}

export function testSentryIntegration() {
  captureMessage('Sentry test event', 'info')
  console.log('Sentry test event sent')
}
